import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CaretRight, CaretDown, CalendarBlank, X, FileText } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SellerSalesReport from "@/components/SellerSalesReport";
import PaymentsReceivedReport from "@/components/PaymentsReceivedReport";

const STATUS_LABEL = { draft: "Borrador", sent: "Enviada", paid: "Pagada", overdue: "Vencida" };
const STATUS_COLOR = { draft: "#52525B", sent: "#1E88E5", paid: "#00873D", overdue: "#FFC000" };

const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const startOfMonth = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() + offset); d.setDate(1);
  return toISO(d);
};
const endOfMonth = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() + offset + 1); d.setDate(0);
  return toISO(d);
};
const startOfYear = () => toISO(new Date(new Date().getFullYear(), 0, 1));

const PRESETS = [
  { v: "current_month",  label: "Mes en curso", from: () => startOfMonth(0),  to: () => endOfMonth(0)  },
  { v: "last_month",     label: "Mes anterior", from: () => startOfMonth(-1), to: () => endOfMonth(-1) },
  { v: "current_year",   label: "Año en curso", from: startOfYear,            to: () => toISO(new Date()) },
  { v: "all",            label: "Histórico",     from: () => "",              to: () => "" },
  { v: "custom",         label: "Personalizado", from: () => "",              to: () => "" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCur = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${fmt(n)}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

export default function Reports() {
  const [data, setData] = useState(null);
  const [topSold, setTopSold] = useState({ items: [], from_date: null, to_date: null });
  const [preset, setPreset] = useState("current_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [productSales, setProductSales] = useState({});
  const [loadingProductSales, setLoadingProductSales] = useState(null);
  const [openSaleDetail, setOpenSaleDetail] = useState(null);

  useEffect(() => { api.get("/reports/summary").then((r) => setData(r.data)); }, []);

  const loadTopSold = useCallback(async () => {
    const p = PRESETS.find((x) => x.v === preset);
    let from = "", to = "";
    if (preset === "custom") { from = customFrom; to = customTo; }
    else { from = p.from(); to = p.to(); }
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    const { data: res } = await api.get("/reports/top-sold", { params });
    setTopSold(res);
    setExpanded(null);
    setProductSales({});
  }, [preset, customFrom, customTo]);

  useEffect(() => { loadTopSold(); }, [loadTopSold]);

  const toggleExpand = async (pid) => {
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    if (!productSales[pid]) {
      setLoadingProductSales(pid);
      const params = {};
      if (topSold.from_date) params.from_date = topSold.from_date;
      if (topSold.to_date) params.to_date = topSold.to_date;
      try {
        const { data: list } = await api.get(`/reports/product-sales/${pid}`, { params });
        setProductSales((s) => ({ ...s, [pid]: list }));
      } finally {
        setLoadingProductSales(null);
      }
    }
  };

  const openSale = async (saleId) => {
    try {
      const { data: sale } = await api.get(`/sales/${saleId}`);
      setOpenSaleDetail(sale);
    } catch {
      setOpenSaleDetail({ error: true });
    }
  };

  if (!data) return <div className="p-8 text-zinc-500">Cargando…</div>;

  const rangeLabel = () => {
    if (preset === "all") return "Histórico completo";
    if (preset === "custom") return `${customFrom || "?"} → ${customTo || "?"}`;
    const p = PRESETS.find((x) => x.v === preset);
    return `${p.from()} → ${p.to()}`;
  };

  return (
    <div className="p-6 sm:p-8 space-y-8">
      <header>
        <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">ANÁLISIS</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Reportes</h1>
        <p className="text-zinc-500 mt-1 text-sm">Tipo de cambio actual: <span className="numeric">1 USD = {data.exchange_rate} MXN</span></p>
      </header>

      <SellerSalesReport />

      <PaymentsReceivedReport />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total productos", v: data.total_products },
          { label: "Unidades en stock", v: data.total_items },
          { label: "Valor costo (USD)", v: `$${fmt(data.total_cost_usd)}` },
          { label: "Utilidad pot. (USD)", v: `$${fmt(data.potential_profit_usd)}` },
        ].map((k) => (
          <div key={k.label} className="border border-zinc-200 p-5">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">{k.label}</div>
            <div className="numeric text-2xl font-bold mt-2">{k.v}</div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-6">
        <div className="border border-zinc-200 p-6 bg-white">
          <h3 className="font-heading text-lg font-semibold mb-4">Valor por categoría (USD)</h3>
          {data.by_category.length === 0 ? (
            <div className="dotted-bg py-12 text-center text-zinc-400 text-sm">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.by_category} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(v) => `$${fmt(v)} USD`} />
                <Bar dataKey="value_usd" fill="#002FA7" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="border border-zinc-200 bg-white">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading text-lg font-semibold">Productos más vendidos</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1">
              <CalendarBlank size={12}/> {rangeLabel()}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="topsold-filters">
            {PRESETS.map((p) => (
              <button
                key={p.v}
                onClick={()=>setPreset(p.v)}
                className={`text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${preset === p.v ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                data-testid={`preset-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs font-mono-data uppercase text-zinc-500">Desde</label>
              <Input type="date" value={customFrom} onChange={(e)=>setCustomFrom(e.target.value)} className="h-8 w-40" data-testid="custom-from"/>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-mono-data uppercase text-zinc-500">Hasta</label>
              <Input type="date" value={customTo} onChange={(e)=>setCustomTo(e.target.value)} className="h-8 w-40" data-testid="custom-to"/>
            </div>
            {(customFrom || customTo) && (
              <button onClick={()=>{ setCustomFrom(""); setCustomTo(""); }} className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1">
                <X size={12}/> Limpiar
              </button>
            )}
          </div>
        )}

        <table className="w-full text-sm" data-testid="top-sold-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3 w-8"></th>
              <th className="text-left px-4 py-3 w-8">#</th>
              <th className="text-left px-4 py-3">Producto</th>
              <th className="text-right px-4 py-3">Notas</th>
              <th className="text-right px-4 py-3">Unidades vendidas</th>
              <th className="text-right px-4 py-3">Ingresos (USD)</th>
            </tr>
          </thead>
          <tbody>
            {topSold.items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-zinc-400 dotted-bg">Sin ventas en este rango</td></tr>
            )}
            {topSold.items.map((t, i) => {
              const isOpen = expanded === t.product_id;
              return (
                <>
                  <tr
                    key={t.product_id}
                    className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={()=>toggleExpand(t.product_id)}
                    data-testid={`top-sold-row-${t.product_id}`}
                  >
                    <td className="px-4 py-3 text-zinc-400">
                      {isOpen ? <CaretDown size={14} weight="bold"/> : <CaretRight size={14} weight="bold"/>}
                    </td>
                    <td className="px-4 py-3 numeric text-zinc-400">{i+1}</td>
                    <td className="px-4 py-3 font-medium">{t.product_name}</td>
                    <td className="px-4 py-3 text-right numeric text-zinc-600">{t.notes_count}</td>
                    <td className="px-4 py-3 text-right numeric font-semibold">{t.qty}</td>
                    <td className="px-4 py-3 text-right numeric text-[#00873D]">${fmt(t.revenue_usd)}</td>
                  </tr>
                  {isOpen && (
                    <tr key={`${t.product_id}-detail`} className="bg-zinc-50/60">
                      <td colSpan={6} className="px-6 py-4">
                        {loadingProductSales === t.product_id ? (
                          <div className="text-xs text-zinc-500">Cargando notas…</div>
                        ) : (productSales[t.product_id] || []).length === 0 ? (
                          <div className="text-xs text-zinc-500">Sin notas en el rango.</div>
                        ) : (
                          <table className="w-full text-xs border border-zinc-200 bg-white" data-testid={`product-sales-${t.product_id}`}>
                            <thead className="bg-zinc-100 text-[10px] uppercase tracking-wider text-zinc-500">
                              <tr>
                                <th className="text-left px-3 py-2">Folio</th>
                                <th className="text-left px-3 py-2">Fecha</th>
                                <th className="text-left px-3 py-2">Cliente</th>
                                <th className="text-left px-3 py-2">Estatus</th>
                                <th className="text-right px-3 py-2">Cant.</th>
                                <th className="text-right px-3 py-2">P.U.</th>
                                <th className="text-right px-3 py-2">Subtotal</th>
                                <th className="text-right px-3 py-2 w-20"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(productSales[t.product_id] || []).map((sn) => (
                                <tr key={sn.sale_id} className="border-t border-zinc-100">
                                  <td className="px-3 py-2 font-mono-data font-semibold">#{String(sn.folio).padStart(6,"0")}</td>
                                  <td className="px-3 py-2 text-zinc-600">{fmtDate(sn.movement_at)}</td>
                                  <td className="px-3 py-2">{sn.client_name}</td>
                                  <td className="px-3 py-2">
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: STATUS_COLOR[sn.status]+"20", color: STATUS_COLOR[sn.status] }}>
                                      {STATUS_LABEL[sn.status] || sn.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right numeric font-semibold">{sn.qty}</td>
                                  <td className="px-3 py-2 text-right numeric">{fmtCur(sn.unit_price, sn.currency)}</td>
                                  <td className="px-3 py-2 text-right numeric">{fmtCur(sn.subtotal, sn.currency)}</td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      onClick={()=>openSale(sn.sale_id)}
                                      className="text-[#002FA7] hover:underline inline-flex items-center gap-1"
                                      data-testid={`view-sale-${sn.sale_id}`}
                                    >
                                      <FileText size={11}/> Ver
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </section>

      <SaleDetailDialog sale={openSaleDetail} onClose={()=>setOpenSaleDetail(null)} />
    </div>
  );
}

function SaleDetailDialog({ sale, onClose }) {
  if (!sale) return null;
  if (sale.error) {
    return (
      <Dialog open onOpenChange={(o)=>{ if (!o) onClose(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Error</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-600">No se pudo cargar la nota.</div>
        </DialogContent>
      </Dialog>
    );
  }
  const status = sale.status;
  const snap = sale.client_snapshot || {};
  return (
    <Dialog open onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="report-sale-detail">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            Nota #{String(sale.folio).padStart(6,"0")}
            <span className="text-[10px] font-semibold px-2 py-1 rounded" style={{ background: STATUS_COLOR[status]+"20", color: STATUS_COLOR[status] }}>
              {STATUS_LABEL[status] || status}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Cliente</div>
              <div className="font-semibold mt-1">{snap.display_name || snap.name || "—"}</div>
              {snap.business_name && <div className="text-xs text-zinc-500">{snap.business_name}</div>}
              {snap.city && <div className="text-xs text-zinc-500">{snap.city}</div>}
            </div>
            <div>
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">F. Factura</div>
              <div className="mt-1">{sale.invoice_date}</div>
              <div className="text-[10px] font-mono-data uppercase text-zinc-500 mt-2">F. Vencimiento</div>
              <div>{sale.due_date}</div>
            </div>
          </div>

          <div className="border border-zinc-200">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">Producto</th>
                  <th className="text-right px-3 py-2">Cant.</th>
                  <th className="text-right px-3 py-2">P.U.</th>
                  <th className="text-right px-3 py-2">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(sale.items || []).map((it, i) => (
                  <tr key={`${it.product_id || it.custom_name || "i"}-${i}`} className="border-t border-zinc-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.product_name || it.custom_name}</div>
                      {it.custom_description && <div className="text-[11px] text-zinc-500">{it.custom_description}</div>}
                      {it.is_custom && <span className="text-[10px] font-bold text-[#a87a00] bg-[#FFC000]/20 rounded px-1.5 py-0.5">LIBRE</span>}
                    </td>
                    <td className="px-3 py-2 text-right numeric">{it.qty}</td>
                    <td className="px-3 py-2 text-right numeric">{fmtCur(it.unit_price, it.currency)}</td>
                    <td className="px-3 py-2 text-right numeric font-semibold">{fmtCur(it.subtotal, it.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="border border-zinc-200 p-3">
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Total</div>
              <div className="numeric text-lg font-bold mt-1">{fmtCur(sale.total, sale.currency)}</div>
            </div>
            <div className="border border-[#00873D]/30 bg-[#00873D]/5 p-3">
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Pagado</div>
              <div className="numeric text-lg font-bold text-[#00873D] mt-1">{fmtCur(sale.paid_total, sale.currency)}</div>
            </div>
            <div className={`border p-3 ${sale.outstanding > 0.005 ? "border-[#E0000F]/30 bg-[#E0000F]/5" : "border-zinc-200"}`}>
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Saldo</div>
              <div className={`numeric text-lg font-bold mt-1 ${sale.outstanding > 0.005 ? "text-[#E0000F]" : ""}`}>{fmtCur(sale.outstanding, sale.currency)}</div>
            </div>
          </div>

          {(sale.payments || []).length > 0 && (
            <div>
              <div className="text-[10px] font-mono-data uppercase text-zinc-500 mb-2">Pagos</div>
              <table className="w-full text-xs border border-zinc-200">
                <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Banco</th>
                    <th className="text-right px-3 py-2">Monto</th>
                    <th className="text-left px-3 py-2">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {sale.payments.map((p) => (
                    <tr key={p.id} className="border-t border-zinc-100">
                      <td className="px-3 py-2 text-zinc-600">{fmtDate(p.created_at)}</td>
                      <td className="px-3 py-2 font-medium">{p.bank_name}</td>
                      <td className="px-3 py-2 text-right numeric">{fmtCur(p.amount, p.currency)}</td>
                      <td className="px-3 py-2 text-zinc-500">{p.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {sale.notes && (
            <div className="text-xs text-zinc-600 border-l-2 border-zinc-300 pl-3">
              <div className="text-[10px] font-mono-data uppercase text-zinc-500 mb-1">Notas</div>
              {sale.notes}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={onClose} className="bg-[#002FA7] hover:bg-[#002277] text-white">Cerrar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
